import { memo, type PropsWithChildren, type SyntheticEvent } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getFormInputProps } from "util/forms";
import { selectSetupActiveCompany } from "features/general/selectors";
import { Checkbox } from "ui/forms/formik-elements";
import StatefulSelector from "ui/widgets/StatefulSelector";
import { CountryIso2 } from "ui/widgets/CountrySelector";
import { FormField, type IbanXSBanksRecipientsPivotProps } from "./types";

const IbanXSBanksRecipientsPivot = ({
    values,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isBootstrapDataLoading,
    bootstrapData,
    children,
    initialValues,
    ...restProps
}: PropsWithChildren<IbanXSBanksRecipientsPivotProps>) => {
    const onAutocompleteChange = (formField: string) =>
        (_: SyntheticEvent, values: string | Array<string>) => {
            setFieldValue(formField, values, true);
            setTimeout(() => setFieldTouched(formField, true));
        };

    return (
        <Box
            component='form'
            onSubmit={handleSubmit}
            autoComplete='off'
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 2
                }}
            >
                <TextField
                    fullWidth
                    required
                    disabled
                    size='small'
                    name={FormField.BankId}
                    value={initialValues[FormField.BankId]}
                    label='Bank ID'
                />
                <TextField
                    fullWidth
                    required
                    disabled
                    size='small'
                    name={FormField.RecipientId}
                    value={initialValues[FormField.RecipientId]}
                    label='Recipient ID'
                />
                <StatefulSelector
                    fullWidth
                    disableClearable
                    selectOptions={selectSetupActiveCompany}
                    size='small'
                    layout={null}
                    loading={isBootstrapDataLoading}
                    disabled={isBootstrapDataLoading}
                    value={values[FormField.Mor] || null}
                    onChange={onAutocompleteChange(FormField.Mor)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.Mor, restProps),
                        name: FormField.Mor,
                        label: 'Domain'
                    }}
                />
                <CountryIso2
                    fullWidth
                    disableClearable
                    value={values[FormField.Iso2]}
                    onChange={onAutocompleteChange(FormField.Iso2)}
                    TextFieldProps={{
                        ...getFormInputProps(FormField.Iso2, restProps),
                        name: FormField.Iso2,
                        label: 'Countries Available'
                    }}
                />
                <Checkbox
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    label='Enabled'
                    handleBlur={handleBlur}
                    CheckboxProps={{
                        inputProps: {
                            'aria-label': 'Enabled'
                        },
                        name: FormField.IsEnabled,
                        checked: values[FormField.IsEnabled]
                    }}
                />
            </Box>
            {children}
        </Box>
    );
};

export default memo(IbanXSBanksRecipientsPivot);
